import React, { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Lock from '@assets/img/icons/lock.svg?react';

import { type SpecContractType } from '@/common/types/contract';
import { ModalContext } from '@/components/Modals/Modal/config';
import { StringMain, StringSmall } from '@/components/Typography/Typography';
import { MainButton } from '@/components/ui/Button/Button';
import { changeTutorial } from '@/redux/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '@/redux/store';

import shinePng from '/img/circle-light-effect.png';
import shineWebp from '/img/circle-light-effect.webp';
import cashPng from '/img/icons/cash-large.png';
import cashWebp from '/img/icons/cash-large.webp';
import customizablePng from '/img/icons/custom_contract/customizable.png';
import customizableWebp from '/img/icons/custom_contract/customizable.webp';
import folderPng from '/img/icons/custom_contract/folder.png';
import folderWebp from '/img/icons/custom_contract/folder.webp';
import dicePng from '/img/icons/dice.png';
import diceWebp from '/img/icons/dice.webp';
import timeLimitedPng from '/img/icons/golden_contract/timeLimited.png';
import timeLimitedWebp from '/img/icons/golden_contract/timeLimited.webp';
import truckPng from '/img/icons/golden_contract/truck.png';
import truckWebp from '/img/icons/golden_contract/truck.webp';
import starPng from '/img/icons/star-large.png';
import starWebp from '/img/icons/star-large.webp';

type InfoBarItem = {
  img: string;
  imgWebp: string;
  textYellow?: string;
  textWhite: string;
};

interface TutorialProps {
  tutorial: SpecContractType;
}

const Tutorial: React.FC<TutorialProps> = ({ tutorial }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userId = useAppSelector((state) => state.user.user?.id);
  const { closeModal } = useContext(ModalContext);
  const isGolden: boolean = tutorial === 'Golden';
  const infoBars: InfoBarItem[] = [
    {
      img: cashPng,
      imgWebp: cashWebp,
      textYellow: isGolden ? '+50%' : '+20%',
      textWhite: 'CASH',
    },
    {
      img: starPng,
      imgWebp: starWebp,
      textYellow: isGolden ? '+50%' : '+20%',
      textWhite: 'Star Points',
    },
    {
      img: dicePng,
      imgWebp: diceWebp,
      textWhite: t('Dice in rewards'),
    },
    {
      img: isGolden ? timeLimitedPng : customizablePng,
      imgWebp: isGolden ? timeLimitedWebp : customizableWebp,
      textWhite: isGolden ? t('Time Limited') : t('Customizable'),
    },
  ];

  return (
    <div className="flex flex-col items-center self-stretch -mt-0.5 gap-3 flex-1">
      <div className="relative w-[11.25rem] h-40 min-h-40">
        <picture className="relative z-[1]">
          <source
            srcSet={isGolden ? truckWebp : folderWebp}
            type="image/webp"
          />
          <img src={isGolden ? truckPng : folderPng} alt="resource" />
        </picture>
        <div className="absolute top-[50%] left-[50%] mt-2 w-80 h-[14.25rem] translate-x-[-50%] translate-y-[-50%] z-[-3]">
          <picture>
            <source srcSet={shineWebp} type="image/webp" />
            <img src={shinePng} alt="shine" className="h-full w-full" />
          </picture>
        </div>
        <div className="bg-black rounded-full h-10 w-[12.5rem] absolute bottom-2 left-[50%] -translate-x-1/2 blur-xl" />
        <div className="!min-w-[12.25rem] bg-[#1D1F25] bg-opacity-60 border border-white/40 backdrop-blur-xl rounded-[80px] flex gap-1 pb-2 pt-1.5 px-1 items-center justify-center absolute bottom-[1.125rem] left-[50%] translate-x-[-50%] z-[4]">
          <Lock className="size-4 min-w-4 text-white/40" />
          <StringMain weight="medium" className="text-white/60">
            {t('Available at')}
          </StringMain>
          <StringMain weight="medium">
            {t('Level')} {isGolden ? '7' : '6'}
          </StringMain>
        </div>
      </div>
      {isGolden ? (
        <StringSmall className="text-center !text-white/60 max-w-[300px] !leading-[120%]">
          <Trans
            i18nKey={
              'Your chance to reach a new income level. <1 /> Ready for a real profit hunt?'
            }
            components={{ 1: <br /> }}
          />
        </StringSmall>
      ) : (
        <StringSmall className="text-center text-white/60 max-w-[300px] !leading-[120%]">
          {t('Make your own contract.Ready to grab the best deal?')}
        </StringSmall>
      )}
      <div className="flex flex-col gap-1 w-full px-12 mt-3">
        {infoBars.map((item, index) => (
          <div
            key={index}
            className="flex items-center justify-between bg-white/10 rounded-lg py-1 pl-2 pr-4"
          >
            <div className="flex items-center gap-2">
              <picture>
                <source srcSet={item.imgWebp} type="image/webp" />
                <img src={item.img} alt="icon" className="w-[32px] h-[32px]" />
              </picture>
              <StringSmall>
                {item.textYellow && (
                  <span className="text-yellow-300 mr-1">
                    {item.textYellow}
                  </span>
                )}
                <span className="text-white">{item.textWhite}</span>
              </StringSmall>
            </div>
            <Lock className="size-4 min-w-4 text-white/40" />
          </div>
        ))}
      </div>
      <div className="px-4 w-full mb-10 mt-auto pt-5">
        <MainButton
          onClick={() => {
            dispatch(
              changeTutorial({
                userId: userId || '',
                kind: tutorial,
              }),
            );
            closeModal();
          }}
          text={t('Got it!')}
        />
      </div>
    </div>
  );
};

export default Tutorial;
